import { ReactNode } from 'react';
import { useKeycloak } from '@sphere/auth/data-access';
import styles from './ProtectedRoute.module.scss';

interface ProtectedRouteProps {
  children: ReactNode;
  roles?: string[];
  requireAll?: boolean; // Si true, l'utilisateur doit avoir TOUS les rôles
}

/**
 * Composant de protection de route
 *
 * ⚠️ SÉCURITÉ : Ce composant vérifie les rôles côté frontend uniquement pour l'UX.
 * Le backend DOIT TOUJOURS valider les rôles dans les requêtes API.
 *
 * Les rôles sont récupérés depuis le token JWT signé par Keycloak (non falsifiable).
 * Cependant, un utilisateur malveillant pourrait modifier le store Zustand ou localStorage
 * pour contourner cette vérification frontend. C'est pourquoi le backend doit toujours
 * valider les rôles depuis le token JWT dans chaque requête.
 */
export function ProtectedRoute({
  children,
  roles = [],
  requireAll = false,
}: ProtectedRouteProps) {
  const { authenticated, initialized, userInfo, tokenParsed } = useKeycloak();

  // Attendre l'initialisation (Keycloak gère automatiquement la redirection avec onLoad: 'login-required')
  if (!initialized) {
    return (
      <div className={styles.loaderContainer}>
        <div className={styles.spinner} />
      </div>
    );
  }

  // Si pas authentifié, Keycloak aura déjà redirigé l'utilisateur
  // On affiche juste un loader au cas où
  if (!authenticated) {
    return (
      <div className={styles.loaderContainer}>
        <div className={styles.loaderContent}>
          <div className={`${styles.spinner} ${styles.spinnerCentered}`} />
          <p className={styles.loaderText}>Authentification en cours...</p>
        </div>
      </div>
    );
  }

  // Vérification des rôles si spécifiés
  // ⚠️ IMPORTANT : Utiliser tokenParsed en priorité car il vient directement du token JWT signé
  // userInfo peut être manipulé côté client, mais tokenParsed est extrait du token signé
  if (roles.length > 0) {
    // Récupérer les rôles depuis le token parsé (source de vérité signée)
    const tokenRoles = tokenParsed?.realm_access?.roles || [];
    // Fallback sur userInfo si tokenParsed n'est pas disponible (ne devrait pas arriver)
    const userRoles = userInfo?.realmRoles || tokenRoles;

    const hasRequiredRoles = requireAll
      ? roles.every((role) => userRoles.includes(role))
      : roles.some((role) => userRoles.includes(role));

    if (!hasRequiredRoles) {
      return (
        <div className={styles.loaderContainer}>
          <div className={styles.loaderContent}>
            <h1 className={styles.forbiddenTitle}>Accès refusé</h1>
            <p className={styles.forbiddenText}>
              Vous n'avez pas les permissions nécessaires pour accéder à cette
              page.
            </p>
          </div>
        </div>
      );
    }
  }

  return <>{children}</>;
}
